class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();
    this.header = this.closest("[data-header]");
    this.headerWrapper = this.closest(".shopify-section-header");
    if (this.header) {
      this.inverse = !!this.header.classList.contains("header--inverse");

      // iPhone fix
      if (navigator.platform === "iPhone") {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }

      // extra events
      document.addEventListener("keyup", this.onKeyUp.bind(this));
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!this.header.classList.contains("header--is-open")) {
      return;
    }
    if (event?.code.toUpperCase() !== "ESCAPE") {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    if (this.header) {
      this.header.classList.add("header--is-open");
      this.inverse && this.header.classList.add("header--inverse-solid");
    }
    if (this.headerWrapper) {
      this.headerWrapper.classList.add("shopify-section-header--open");
    }
    // Switch aria-expanded
    if (this.options.ariaExpanded) {
      document
        .querySelectorAll("toggle-mobile-navigation")
        .forEach((button) => {
          button.setAttribute("aria-expanded", true);
        });
    }
    trapFocus(this.header);
  }

  remove() {
    super.remove();
    if (this.header) {
      this.header.classList.remove("header--is-open");
      this.inverse && this.header.classList.remove("header--inverse-solid");
    }
    if (this.headerWrapper) {
      this.headerWrapper.classList.remove("shopify-section-header--open");
    }
    // Switch aria-expanded
    if (this.options.ariaExpanded) {
      document
        .querySelectorAll("toggle-mobile-navigation")
        .forEach((button) => {
          button.setAttribute("aria-expanded", false);
        });
    }
    this.closeAllSibblings();
    removeTrapFocus();
  }

  closeAllSibblings() {
    const collapsibles = this.target.querySelector("collapsible-element");
    collapsibles.closeAll();
  }
}

class StickyHeader extends HTMLElement {
  constructor() {
    super();

    // select element
    this.header = document.querySelector("[data-header]");
    this.headerWrapper = document.querySelector(".shopify-section-header");
    this.announcementBar = document.querySelector(
      ".shopify-section-announcement-bar"
    );
    this.headerElement = this;
    this.headerInverse =
      !!this.headerElement.classList.contains("header--inverse");
    this.headerNav = this.querySelector("collapsible-header");
    this.mobileNav = this.querySelector("toggle-mobile-navigation");
    this.search = this.querySelector("[data-header-search]");
    this.productInfo = document.querySelector("[data-product-info]");
    this.submenu = document.querySelector("[data-submenu-trigger]");
    this.mainMenuItems = this.querySelectorAll("[data-main-menu-item]");
    const submenuItems = document.querySelectorAll("[data-submenu-item]");
    const submenuItemsLevel3Targets = document.querySelectorAll(
      "[data-submenu-level3-target]"
    );
    const body = document.querySelector("body");

    this.mainMenuItems.forEach(function (mainMenuItem) {
      /*
        ** Calculate height on hovering level 3 submenu for the submenu
        */
      if (mainMenuItem.querySelector("[data-submenu-trigger]") !== null) {
        const submenuTrigger = mainMenuItem.querySelector("[data-submenu-trigger]");
        const submenu = mainMenuItem.querySelector("[data-submenu-item]");
        const level3Trigger = submenu.querySelector("[data-submenu-level3-trigger]");
        level3Trigger.addEventListener("mouseover", () => {
          const menuHeight =
            submenu.querySelector("[data-submenu-level3-target]").offsetHeight +
            60;
          if (submenuTrigger) {
            submenuTrigger.style.minHeight = `${menuHeight}px`;
          }
        });
      }

    });

    /*
     ** Submenu
     */
    submenuItems.forEach(function (submenuItem) {
      const submenuLevel3Trigger = submenuItem.querySelector(
        "[data-submenu-level3-trigger]"
      );
      const submenuLevel3Target = submenuItem.querySelector(
        "[data-submenu-level3-target]"
      );
      if (submenuLevel3Trigger && submenuLevel3Target) {
        submenuLevel3Trigger.addEventListener("mouseover", () => {
          // close all submenus
          submenuItemsLevel3Targets.forEach(function (
            submenuItemsLevel3Target
          ) {
            submenuItemsLevel3Target.classList.remove("level3--is-open");
          });
          // open the right submenu
          submenuLevel3Target.classList.add("level3--is-open");
        });
      }
    });

    if (this.headerWrapper) {
      // scroll parameters
      this.currentScrollTop = 0;
      this.scrollOffset = 0;
      this.headerStart = 0;

      if (this.announcementBar) {
        const announcementBarHeight = this.announcementBar.offsetHeight;
        this.scrollOffset += announcementBarHeight;
        this.headerStart += announcementBarHeight;
      }

      if (this.headerElement) {
        this.scrollOffset += this.headerElement.offsetHeight;
      }

      this.updateMegaMenuTop();
      window.addEventListener("resize", this.updateMegaMenuTop.bind(this));

      // scroll event
      this.raf = null; // Safe request animation frame
      (this.timer = null), // Safe scroll timer
        window.addEventListener(
          "scroll",
          () => {
            clearTimeout(this.timer);
            if (!this.raf) {
              // A request animation frame is used to animate on max 60fps.
              this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
            }
            this.timer = setTimeout(() => {
              // Stop animation, cancel request animation frame
              cancelAnimationFrame(this.raf);
              this.raf = null;
            }, 50);
          },
          false
        );
    }
  }

  scrollAnimation() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    // only animate if header is closed.
    if (!this.header?.classList?.contains("header--is-open")) {
      if (scrollTop + window.innerHeight > document.body.clientHeight) {
        this.hide();
      } else if (
        scrollTop > this.currentScrollTop &&
        scrollTop > this.scrollOffset
      ) {
        this.hide();
      } else if (scrollTop <= this.headerStart) {
        this.revealTop();
      } else if (
        scrollTop < this.currentScrollTop &&
        scrollTop > this.headerStart
      ) {
        this.reveal();
      }
    }

    this.currentScrollTop = scrollTop;

    this.updateMegaMenuTop();

    // Recall request animation loop
    this.raf = requestAnimationFrame(this.scrollAnimation.bind(this));
  }

  hide() {
    this.headerWrapper.classList.add("shopify-section-header--sticky");
    this.closeHeaderNav();
    this.closeMobileNav();
    this.closeSearch();
    this.productInfoPosition("up");
  }

  reveal() {
    this.headerWrapper.classList.add(
      "shopify-section-header--sticky",
      "shopify-section-header--animate"
    );
    this.headerWrapper.classList.remove("shopify-section-header--hidden");
    if (this.headerInverse) {
      this.headerElement.classList.add("header--inverse-solid");
    }
    this.productInfoPosition("down");
  }

  revealTop() {
    if (this.headerInverse && !this.classList.contains("header--is-open")) {
      this.headerElement.classList.remove("header--inverse-solid");
    }
    this.headerWrapper.classList.remove(
      "shopify-section-header--hidden",
      "shopify-section-header--sticky",
      "shopify-section-header--animate"
    );
    this.productInfoPosition("up");
  }

  closeHeaderNav() {
    if (!this.headerNav || window.innerWidth < 768) {
      return false;
    }
    this.headerNav.closeAll();
  }

  closeMobileNav() {
    if (!this.mobileNav) {
      return false;
    }
    this.mobileNav.remove();
  }

  closeSearch() {
    if (!this.search) {
      return false;
    }
  }

  productInfoPosition(pos = "up") {
    if (!this.productInfo) {
      return;
    }
    if (pos == "down") {
      this.productInfo.classList.add("product-info-col--sticky-with-header");
    } else {
      this.productInfo.classList.remove("product-info-col--sticky-with-header");
    }
  }

  getHeaderHeight() {
    const sticky = document.querySelector("sticky-header");
    if (sticky) {
      const rect = sticky.getBoundingClientRect();
      return Math.max(0, Math.round(rect.bottom));
    }
    const wrapper = this.headerWrapper || document.querySelector(".shopify-section-header");
    if (wrapper) {
      const rect = wrapper.getBoundingClientRect();
      return Math.max(0, Math.round(rect.bottom));
    }
    return 0;
  }

  updateMegaMenuTop() {
    const top = this.getHeaderHeight();
    document.documentElement.style.setProperty("--mega-menu-top", `${top}px`);
    return top;
  }
}

if (!customElements.get("toggle-mobile-navigation")) {
  customElements.define("toggle-mobile-navigation", ToggleMobileNavigation);
}

if (!customElements.get("sticky-header")) {
  customElements.define("sticky-header", StickyHeader);
}

(function () {
  function computeTop() {
    const sticky = document.querySelector("sticky-header");
    const el = sticky || document.querySelector(".shopify-section-header") || document.querySelector("[data-header]");
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const top = Math.max(0, Math.round(rect.bottom));
    document.documentElement.style.setProperty("--mega-menu-top", `${top}px`);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", computeTop, { once: true });
  } else {
    computeTop();
  }
  window.addEventListener("resize", computeTop);
})();
